import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { extractErrorMessage } from '../api';
import { BetaReaderShell } from '../components/BetaReaderShell';
import { CopyLinkControl } from '../components/CopyLinkControl';
import { FeedV2Results } from '../components/FeedV2Results';
import { HomeSwitch } from '../components/HomeSwitch';
import { IconCheck, IconLink } from '../components/icons';
import { SearchWindowToggle } from '../components/SearchWindowToggle';
import { SortToggle } from '../components/SortToggle';
import { V2SearchBar, type V2SearchMethod } from '../components/V2SearchBar';
import { useReaderSearchTelemetry } from '../hooks/useReaderSearchTelemetry';
import { useSearchFeedSave } from '../hooks/useSearchFeedSave';
import { useReaderRoutes } from '../providers/ReaderRoutesProvider';
import { useAdminMode, useUser } from '../providers/UserProvider';
import { streamSearchV2 } from '../repositories/feedsV2Repository';
import { resolveSearchId } from '../repositories/savedSearchRepository';
import { buildComplete, INITIAL_STATE, type FeedV2State } from '../utils/feedsV2';
import type { BetaSearchSort } from '../utils/betaSearchSort';
import { windowFromParam, WINDOW_OPTIONS, type WindowHours } from '../utils/feedWindow';
import { searchIdPath, searchPagePath } from '../utils/readerRoutes';

/**
 * A search on the feeds v2 engine: the text built exactly as a saved feed of
 * it would be, so what it shows is what Save feed keeps. Its URL names the
 * search by its description's id (`/search/<id>`, read back to text; an old
 * run's id opens under its description's). A typed search starts at
 * `/search?q=…`, so nothing waits on naming it, and once its build names the
 * description the URL becomes the id's in place, without searching again.
 * Saving stores the text as a feed (Update feed when the reader already has
 * one with this prompt), with a Show in Home switch; the page stays on the
 * results and links to the feed. Each search is one telemetry attempt, named
 * by its search-history entry once it starts reading. Signed out, the search
 * runs too (admitted per visitor, as the snapshot reader's anonymous search
 * was), and Save feed asks for an account first, then saves (through
 * `readerPendingAction`).
 */
export default function SearchV2Page() {
  const routes = useReaderRoutes();
  const navigate = useNavigate();
  const location = useLocation();
  const { searchId } = useParams();
  const [params] = useSearchParams();
  // The search an id names, once read or once a text search's build named it.
  const [named, setNamed] = useState<{ id: string; text: string } | null>(null);
  const [lookupError, setLookupError] = useState('');
  const query = (searchId ? (named?.id === searchId ? named.text : '') : (params.get('q') ?? '')).trim();
  const hours: WindowHours = windowFromParam(params.get('hours'));
  const { isAdmin } = useUser();
  const { adminMode } = useAdminMode();
  const [state, setState] = useState<FeedV2State>(INITIAL_STATE);
  const [runKey, setRunKey] = useState(0);
  // Sort is the page's own, not the URL's: every URL change here is a search attempt.
  const [sort, setSort] = useState<BetaSearchSort>(null);
  const feedSave = useSearchFeedSave(query);
  // An id lookup must resolve the query and legacy window before starting its attempt.
  const telemetry = useReaderSearchTelemetry(query.length > 1, query, null);
  // Renaming the URL is no new search: the current attempt goes with it.
  const renameUrl = (path: string, attempt = telemetry.current.current) =>
    navigate(path, { replace: true, state: attempt ? telemetry.navigationState(attempt) : location.state });

  useEffect(() => {
    if (!searchId || named?.id === searchId) return;
    let disposed = false;
    setLookupError('');
    resolveSearchId(searchId).then(search => {
      if (disposed) return;
      setNamed({ id: search.descriptionId, text: search.description });
      // An old run's id: its description's, keeping a 72-hour run's window.
      if (search.descriptionId !== searchId) navigate(searchIdPath(search.descriptionId, hours === 72 || (search.windowHours ?? 0) >= 72), { replace: true });
    }).catch(err => { if (!disposed) setLookupError(extractErrorMessage(err)); });
    return () => { disposed = true; };
  }, [searchId]);

  const builtDescriptionId = state.feed?.description_id;
  useEffect(() => {
    if (searchId || !builtDescriptionId) return;
    setNamed({ id: builtDescriptionId, text: query });
    renameUrl(searchIdPath(builtDescriptionId, hours === 72));
  }, [builtDescriptionId]);

  useEffect(() => {
    setState(INITIAL_STATE);
    if (query.length <= 1) return;
    const controller = new AbortController();
    streamSearchV2({ query, hours, signal: controller.signal, onUpdate: setState }).catch((err) => {
      if (!controller.signal.aborted) setState((prev) => ({ ...prev, done: true, error: extractErrorMessage(err) }));
    });
    return () => controller.abort();
  }, [query, hours, runKey]);

  useEffect(() => {
    if (!state.done || query.length <= 1) return;
    const attempt = telemetry.current.current;
    if (attempt && state.feed) attempt.descriptionId = state.feed.description_id;
    // Signed out, a search has no history entry, so no run id; its story count still counts.
    // A build the judge or grader failed is a failed search, as the page shows it.
    telemetry.finish(state.feed?.run_id ?? null, buildComplete(state) ? state.stories.length : null);
  }, [state.done]);

  useEffect(() => { document.title = query ? `${query} · Search` : 'Search'; }, [query]);

  // A new search (typed, picked from history, or the window changed) is a new attempt, handed to the URL's next page.
  const searchFor = (text: string, windowHours: WindowHours, method: V2SearchMethod | 'window_toggle') => {
    const attempt = telemetry.begin(method, text, windowHours);
    // The same search again changes no URL, so it runs again here and the attempt it began completes.
    if (text === query && windowHours === hours) { setRunKey(key => key + 1); return; }
    // The same text in another window keeps its id; new text goes by its text until its build names it.
    const path = searchId && text === query ? searchIdPath(searchId, windowHours === 72) : searchPagePath(text, windowHours === 72);
    navigate(path, { replace: method === 'window_toggle', state: telemetry.navigationState(attempt) });
  };
  const retry = () => { telemetry.begin('retry', query, hours); setRunKey((key) => key + 1); };

  const stories = state.stories;
  return (
    <BetaReaderShell feedSwitcher storyHotkeys
      search={<V2SearchBar key={query} initial={query} autoFocus={!query && !searchId} onSearch={(next, method) => searchFor(next, hours, method)} />}>
      <div className="beta-home-controls reader-feed-controls">
        <div className="reader-feed-context"><Link to={routes.home}>← For You</Link></div>
        <div className="beta-search-options-bar" role="toolbar" aria-label="Search options">
          {query.length > 1 && <SortToggle selected={sort} disabled={stories.length === 0} onSelect={setSort} />}
          <SearchWindowToggle label="Search time range" options={WINDOW_OPTIONS} selected={hours}
            disabled={!!searchId && named?.id !== searchId}
            onSelect={next => searchFor(query, next, 'window_toggle')} />
        </div>
      </div>
      {feedSave.message && (
        <div className="reader-toast" role="status">
          {feedSave.message} {(feedSave.saved || feedSave.matchingFeed) && <Link to={routes.feed(feedSave.saved || feedSave.matchingFeed!.id)}>Open feed</Link>}
          <button type="button" aria-label="Dismiss notification" onClick={feedSave.dismissMessage}>×</button>
        </div>
      )}
      {query.length > 1 ? <>
        <div className="reader-results-bar">
          <div className="reader-save-group">
            <button className={`beta-add-feed${feedSave.done ? ' added' : ''}`} type="button"
              disabled={feedSave.saving || feedSave.done || (!state.done && !state.error)} onClick={() => void feedSave.save()}>
              {feedSave.done && !feedSave.saving && <IconCheck size={13} />}{feedSave.label}
            </button>
            <label>Show in Home<HomeSwitch on={feedSave.included} disabled={feedSave.saving} onChange={on => void feedSave.changeIncluded(on)} /></label>
          </div>
          <span className="reader-results-copy"><CopyLinkControl getUrl={() => window.location.href} title="Copy search link" ariaLabel="Copy search link" copiedMessage="Search link copied" errorMessage="Could not copy search link"><IconLink /></CopyLinkControl></span>
          <span className="reader-results-count" title={feedSave.matchingFeed ? `Matches your saved feed “${feedSave.matchingFeed.name}”` : undefined}>
            {feedSave.matchingFeed && !feedSave.saved ? `Matches your saved feed “${feedSave.matchingFeed.name}” · ` : ''}{stories.length} stories
          </span>
        </div>
        {feedSave.error && (
          <p className="beta-error" role="alert">{feedSave.error} <button type="button" aria-label="Dismiss notification" onClick={feedSave.dismissError}>×</button></p>
        )}
        <FeedV2Results
          state={state}
          sort={sort}
          resetKey={`search-v2:${query}:${hours}:${runKey}:${sort}`}
          eventContext={{ surface: 'search', surfaceId: state.feed?.run_id ?? null, properties: { feeds_v2: true, ...telemetry.properties, query } }}
          isAdmin={isAdmin && adminMode}
          onRetry={retry}
        />
      </> : searchId ? <p className="beta-message" role={lookupError ? 'alert' : 'status'}>{lookupError || 'Opening your search…'}</p>
        : <p className="beta-message">Describe what you want to read about.</p>}
    </BetaReaderShell>
  );
}
